import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { feedbackApi, type FeedbackType, type FeedbackStatus } from '../../api/feedback';

const TABS: Array<{ value: FeedbackType; label: string }> = [
  { value: 'feature', label: '✨ Feature Request' },
  { value: 'bug', label: '🐛 Bug Report' },
  { value: 'other', label: '💬 General Feedback' },
];

const STATUSES: FeedbackStatus[] = ['open', 'in_progress', 'resolved', 'closed'];
const STATUS_LABEL: Record<FeedbackStatus, string> = {
  open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed',
};
const STATUS_CLS: Record<FeedbackStatus, string> = {
  open: 'badge-blue', in_progress: 'badge-unpaid', resolved: 'badge-paid', closed: 'badge-gray',
};

export default function AdminFeedbackPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<FeedbackType>('bug');

  const { data: requests = [], isLoading } = useQuery({
    queryKey: ['admin-feedback'],
    queryFn: feedbackApi.listAll,
    staleTime: 30 * 1000,
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: FeedbackStatus }) => feedbackApi.updateStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-feedback'] }),
  });

  const filtered = requests.filter(r => r.type === tab);

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Feedback Requests</h1>
      </div>

      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {TABS.map(t => {
          const count = requests.filter(r => r.type === t.value).length;
          return (
            <button
              key={t.value} type="button"
              onClick={() => setTab(t.value)}
              style={{
                padding: '0.5rem 1rem', borderRadius: 8, cursor: 'pointer',
                border: `2px solid ${tab === t.value ? 'var(--primary)' : 'var(--border)'}`,
                background: tab === t.value ? 'var(--primary-light)' : 'var(--surface)',
                color: tab === t.value ? 'var(--primary)' : 'var(--text-2)',
                fontWeight: tab === t.value ? 700 : 500, fontSize: '0.875rem',
              }}
            >{t.label} <span style={{ opacity: 0.7 }}>({count})</span></button>
          );
        })}
      </div>

      <div className="card">
        {isLoading ? <div className="loading-overlay"><span className="spinner" /></div>
          : filtered.length === 0
          ? <div className="empty-state"><div className="empty-state-icon">📭</div><div className="empty-state-text">No requests in this tab yet</div></div>
          : (
            <table>
              <thead>
                <tr>
                  <th>Request #</th><th>Company</th><th>Submitted By</th>
                  <th>{tab === 'bug' ? 'Category' : ''}</th><th>Message</th><th>Date</th><th style={{ width: 160 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{r.requestNumber}</td>
                    <td>{r.companyName}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.83rem' }}>{r.submitterEmail}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.83rem' }}>{r.category ?? '—'}</td>
                    <td style={{ maxWidth: 340, fontSize: '0.85rem' }}>{r.message}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: '0.83rem', whiteSpace: 'nowrap' }}>
                      {new Date(r.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td>
                      <select
                        value={r.status}
                        onChange={e => updateStatus.mutate({ id: r.id, status: e.target.value as FeedbackStatus })}
                        className={`badge ${STATUS_CLS[r.status]}`}
                        style={{ border: 'none', cursor: 'pointer' }}
                      >
                        {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>
    </div>
  );
}
